import { Modal } from "@components/Modal";
import { useTranslatedKey } from "@helpers/useTranslatedKey";
import { useTranslation } from "@helpers/useTranslation";
import { FC } from "react";

type Props = {
  show: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  scenarios: BettingScenario[];
  challenge: BettingChallenge;
  loading: boolean;
};

export const CloseBettingModal: FC<Props> = ({ show, onCancel, onConfirm, scenarios, loading }) => {
  const { t } = useTranslation("admin");
  const { getTranslatedValue } = useTranslatedKey();

  return (
    <Modal show={show} onCancel={onCancel} className="z-50">
      <div className="p-4">
        <h3 className="text-lg font-bold mb-4">{t("closeBettingConfirm")}</h3>

        <p className="mb-6 text-base-content/70">{t("closeBettingMessage")}</p>

        <div className="mb-6">
          <h4 className="text-md font-semibold mb-3">{t("bettingSummary")}</h4>
          <p className="text-sm text-base-content/70 mb-4">{t("allOutcomesResolved")}</p>

          <div className="space-y-3">
            {scenarios.map((scenario, index) => {
              const scenarioText = getTranslatedValue(scenario, "scenario");
              const correctOutcome = scenario.correctOutcome;
              const optionKey = `option${correctOutcome}` as keyof BettingScenario;
              const correctOptionText = correctOutcome !== undefined
                ? getTranslatedValue(scenario, optionKey)
                : "Not resolved";

              return (
                <div key={scenario.id || index} className="rounded-lg border border-base-300 bg-base-200 bg-opacity-50 p-3">
                  <p className="font-medium text-sm">{scenarioText}</p>
                  <p className="text-xs text-base-content/70">
                    {t("correctOutcome")}: {correctOutcome !== undefined ? `${t("option")} ${correctOutcome + 1} - ${correctOptionText}` : "Not resolved"}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col">
          <button
            type="button"
            disabled={loading}
            onClick={onConfirm}
            className={`btn inline-flex w-full justify-center rounded-md bg-green-600 px-3 py-2 text-sm border border-green-700 font-semibold text-white shadow-sm hover:bg-success-focus focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${loading ? "loading" : ""}`}
          >
            {t("closeBetting")}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:w-auto"
          >
            {t("cancel")}
          </button>
        </div>
      </div>
    </Modal>
  );
};
